'use client';

import React, { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  title: string;
  description: string;
  category: string;
  isFoodItem: boolean;
  menuLink?: string;
}

interface LightboxModalProps {
  image: GalleryImage | null;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onShare: (platform: string) => void;
}

const LightboxModal = ({ image, onClose, onNext, onPrevious, onShare }: LightboxModalProps) => {
  useEffect(() => {
    if (image) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [image]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!image) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrevious();
      if (e.key === 'ArrowRight') onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [image, onClose, onNext, onPrevious]);

  if (!image) return null;

  return (
    <div
      className="fixed inset-0 z-500 flex items-center justify-center bg-foreground/95 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      {/* Close Button */}
      <button
        type="button"
        onClick={onClose}
        className="fixed right-4 top-4 z-510 rounded-full bg-background/20 p-3 text-primary-foreground backdrop-blur-sm transition-smooth hover:bg-background/30 focus-ring"
        aria-label="Close lightbox"
      >
        <Icon name="XMarkIcon" size={24} />
      </button>

      {/* Previous Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPrevious();
        }}
        className="fixed left-4 top-1/2 z-510 h-12 w-12 -translate-y-1/2 rounded-full bg-background/20 text-primary-foreground backdrop-blur-sm transition-smooth hover:bg-background/30 focus-ring"
        aria-label="Previous image"
      >
        <Icon name="ChevronLeftIcon" size={24} className="mx-auto" />
      </button>

      {/* Next Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="fixed right-4 top-1/2 z-510 h-12 w-12 -translate-y-1/2 rounded-full bg-background/20 text-primary-foreground backdrop-blur-sm transition-smooth hover:bg-background/30 focus-ring"
        aria-label="Next image"
      >
        <Icon name="ChevronRightIcon" size={24} className="mx-auto" />
      </button>

      {/* Image Container */}
      <div className="relative max-h-[85vh] max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative overflow-hidden rounded-lg bg-card shadow-2xl">
          <AppImage
            src={image.src}
            alt={image.alt}
            className="max-h-[70vh] w-auto object-contain"
          />
        </div>

        {/* Image Info */}
        <div className="mt-4 rounded-lg bg-card p-6 shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="font-heading text-2xl font-semibold text-foreground">{image.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{image.description}</p>
              <div className="mt-3 flex items-center space-x-2">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {image.category}
                </span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="ml-4 flex space-x-2">
              <button
                type="button"
                onClick={() => onShare('facebook')}
                className="rounded-lg bg-muted p-2 text-foreground transition-smooth hover:bg-primary hover:text-primary-foreground focus-ring"
                aria-label="Share on Facebook"
              >
                <Icon name="ShareIcon" size={20} />
              </button>
              <button
                type="button"
                onClick={() => onShare('twitter')}
                className="rounded-lg bg-muted p-2 text-foreground transition-smooth hover:bg-primary hover:text-primary-foreground focus-ring"
                aria-label="Share on Twitter"
              >
                <Icon name="ShareIcon" size={20} />
              </button>
              <button
                type="button"
                onClick={() => onShare('whatsapp')}
                className="rounded-lg bg-muted p-2 text-foreground transition-smooth hover:bg-primary hover:text-primary-foreground focus-ring"
                aria-label="Share on WhatsApp"
              >
                <Icon name="ShareIcon" size={20} />
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LightboxModal;
